import axios from 'axios'
import { noop } from 'lodash'
import { TokenSession } from '@/services/session'
import AuthService from '@/services/auth'
import { activateSessionExpired, isSessionBlocked } from '@/services/sessionExpiry'
import { STATUS, logger } from './utils'

const History = (() => {
    let value = []
    const same = (request, config) => request.url === config.url && request.method === config.method
    const push = (config) => value.push(config)
    const remove = (config) => { value = value.filter((request) => !same(request, config)) }
    const exist = (config) => !!value.find((request) => same(request, config))

    return {
        get: () => value,
        push,
        remove,
        exist,
    }
})()

const requestSuccessHandler = (config) => {
    if (isSessionBlocked()) {
        const blockedError = new axios.CanceledError('Session is expired. Request blocked until user signs in again.')
        blockedError.inverbisSessionBlocked = true
        blockedError.config = config
        return Promise.reject(blockedError)
    }

    const { params } = config
    const { length } = History.get()

    if (params?.polling && length) {
        return new Promise(noop)
    }

    History.push(config)
    return config
}

const requestErrorHandler = (error) => Promise.reject(error)

const responseSuccessHandler = (payload = {}) => {
    // Axios interceptor success receives the AxiosResponse directly.
    // Keep backward-compatibility if callers pass { response }.
    const response = payload?.response || payload
    const {
        request = {}, config, data, status,
    } = response || {}
    History.remove(config)
    logger({
        origin: 'API - RESPONSE',
        endpoint: request.responseURL,
        response: JSON.stringify(data),
        status: status || request.status,
    })

    // Some endpoints may return JSON serialized as string (e.g. "{...}").
    // Normalize it so callers can safely destructure response.data.
    const contentType = (response?.headers?.['content-type'] || response?.headers?.['Content-Type'] || '').toLowerCase()
    const isJsonContentType = contentType.includes('application/json') || contentType.includes('+json')
    if (isJsonContentType && typeof response?.data === 'string') {
        const trimmed = response.data.trim()
        if ((trimmed.startsWith('{') && trimmed.endsWith('}'))
            || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                response.data = JSON.parse(trimmed)
            } catch (e) {
                // ignore parsing errors; keep original string
            }
        }
    }
    return response
}

const responseErrorHandler = ({
    error = {},
    client,
}) => {
    const normalizeError = typeof error === 'object' ? error : {}
    const { response = {}, request = {}, config: originalRequest } = normalizeError
    const { status } = response
    const { responseURL: endpoint } = request

    if (originalRequest) History.remove(originalRequest)

    logger({ origin: `API - ERROR ${status}`, endpoint, status })

    const activateExpiredModeAndReject = () => {
        normalizeError.inverbisSessionExpired = true
        activateSessionExpired({ onRelogin: () => AuthService.signout() })
        return Promise.reject(normalizeError)
    }

    const isUnauthorized = !normalizeError.response || normalizeError.response.status === STATUS.UNAUTHORIZED
    if (isUnauthorized && originalRequest && !originalRequest.inverbisRetry) {
        originalRequest.inverbisRetry = true

        // TokenSession may store JSON or raw strings depending on cookie implementation.
        // Don't block refresh solely on cookie parsing; rely on keycloak session state.
        const oldToken = TokenSession.get()
        const kc = typeof AuthService.instance === 'function' ? AuthService.instance() : null
        if (!oldToken && !kc?.token) return activateExpiredModeAndReject()

        return AuthService.refreshToken()
            .then(({ ok, data }) => {
                // keycloak-js exposes `token` on the instance
                const tokenFormatted = TokenSession.formatToken(data)
                if (!(ok && tokenFormatted?.token)) return activateExpiredModeAndReject()

                const authHeaderValue = `${tokenFormatted.type} ${tokenFormatted.token}`
                originalRequest.headers = originalRequest.headers || {}
                if (typeof originalRequest.headers.set === 'function') {
                    // Axios v1 may use AxiosHeaders
                    originalRequest.headers.set('Authorization', authHeaderValue)
                } else {
                    originalRequest.headers.Authorization = authHeaderValue
                }

                const axiosClient = typeof client === 'function' ? client : axios
                return typeof axiosClient.request === 'function'
                    ? axiosClient.request(originalRequest)
                    : axiosClient(originalRequest)
            })
            .catch(() => activateExpiredModeAndReject())
    }

    return Promise.reject(normalizeError)
}

export default {
    request: {
        success: requestSuccessHandler,
        error: requestErrorHandler,
    },
    response: {
        success: responseSuccessHandler,
        error: responseErrorHandler,
    },
}
